/**
 * The scorecard's own name for each period a chart draws, keyed by the instant the chart draws it
 * at: the shape supersetTooltipFormatter()'s `periods` option and the chart host's `periods` detail
 * both take, so a tooltip's header and the host's sr-only table name a period as the scorecard's
 * column header beside them does.
 *
 * A chart built from the scorecard's data draws each period at its start, as UTC midnight in epoch
 * ms (MetricCardContractBuilder::instant()), so an instant is read back as that date in UTC. The
 * name is the one on that period's column header, found by its start date among the headers the
 * scorecard is showing; a period with no column (the projected one past the tab's dates, say) is
 * named by `headerFor`, the scorecard's own header builder, so it reads the same way.
 *
 * Imports nothing, so a node harness can load it and the drawer can import it statically.
 */

/** Intervals whose header's `label` is the period and `sub_label` its year ("September" / "2026"). */
const YEAR_SUFFIXED = new Set(['monthly', 'quarterly', 'yearly']);

/**
 * One header's two lines as one line: "September 2026", "Q3 2026", "2026", and for a week or a day,
 * whose second line is its dates rather than its year, "Week 36, Sep 1 - Sep 7" and
 * "Monday, 01 Sep, 2026". Null for a header with nothing to print.
 *
 * @param {?{interval?: string, label?: ?string, sub_label?: ?string}} header
 * @returns {?string}
 */
export function scorecardPeriodName(header) {
    const parts = [header?.label, header?.sub_label]
        .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
        .filter((part) => part !== '');

    if (parts.length === 0) return null;

    return parts.join(YEAR_SUFFIXED.has(header?.interval) ? ' ' : ', ');
}

/**
 * @param {number[]} instants     The periods drawn, each its start at UTC midnight in epoch ms.
 * @param {string}   interval     The scorecard's interval ('daily' … 'yearly').
 * @param {object[]} headers      The scorecard's column headers ({interval, label, sub_label, start_date}).
 * @param {?Function} [headerFor] (startDate 'YYYY-MM-DD') => a header, for a period with no column.
 * @returns {Object<number, string>} instant => name; an instant it cannot name is left out, so the
 *          reader falls back to its own formatter for that one.
 */
export function scorecardPeriodNames(instants, interval, headers = [], headerFor = null) {
    const byStart = new Map((headers ?? [])
        .filter((header) => header && !header.isPlaceholder && (header.interval ?? interval) === interval)
        .map((header) => [header.start_date, header]));

    return Object.fromEntries((instants ?? []).flatMap((instant) => {
        if (typeof instant !== 'number' || !Number.isFinite(instant)) return [];

        const start = new Date(instant).toISOString().slice(0, 10);
        const header = byStart.get(start) ?? (typeof headerFor === 'function' ? headerFor(start) : null);
        const name = scorecardPeriodName(header ? { interval, ...header } : null);

        return name ? [[instant, name]] : [];
    }));
}
